import React from "react";
import { FaCalendarCheck, FaClock, FaMapMarkerAlt, FaQrcode, FaWater } from "react-icons/fa";
import { LuDownload, LuFileText, LuKey, LuLogOut, LuPencil } from "react-icons/lu";

const BookingLocationLink = ({ booking, className }) => {
  const content = (
    <>
      <FaMapMarkerAlt aria-hidden="true" />
      {booking.location}
    </>
  );

  return booking.locationMapUrl ? (
    <a className={className} href={booking.locationMapUrl} target="_blank" rel="noreferrer">
      {content}
    </a>
  ) : (
    <span className={className}>{content}</span>
  );
};

const BookingTypeMark = ({ booking, t }) => {
  const isGuidedEvent = Boolean(booking.isGuidedEventBooking);
  return (
    <span
      className={`booking-type ${isGuidedEvent ? "booking-type--event" : "booking-type--rental"}`}
      title={
        isGuidedEvent
          ? t("profilePage.sections.upcoming.eventLabel", "Event booking")
          : t("profilePage.sections.upcoming.rentalLabel", "Rental booking")
      }
    >
      {isGuidedEvent ? <FaCalendarCheck aria-hidden="true" /> : <FaWater aria-hidden="true" />}
    </span>
  );
};

export const UpcomingBookingsCard = ({
  bookings,
  isLoading = false,
  error = "",
  onOpenCalendar,
  onOpenReschedule,
  onOpenCancel,
  onOpenQr = () => {},
  t,
}) => (
  <article className="profile-card profile-card--span2 profile-card--scrollable">
    <header>
      <h2>
        <FaClock aria-hidden="true" /> {t("profilePage.sections.upcoming.title", "Upcoming bookings")}
      </h2>
      <button type="button" className="profile-link" onClick={onOpenCalendar}>
        {t("profilePage.sections.upcoming.seeCalendar", "See calendar")}
      </button>
    </header>
    <div className="profile-card__scroll">
      {isLoading ? (
        <p className="profile-empty">
          {t("profilePage.sections.upcoming.loading", "Fetching your bookings...")}
        </p>
      ) : error ? (
        <p className="profile-empty profile-empty--alert">
          {error}
        </p>
      ) : bookings.length ? (
        <ul className="booking-list">
          {bookings.map((booking) => (
            <li key={booking.id}>
              <div className="booking-list__body">
                <h3>{booking.type}</h3>
                <p>
                  {(booking.displayDate ?? booking.date) || ""} · {booking.time} · {booking.duration}
                </p>
                <BookingLocationLink booking={booking} className="booking-location" />
              </div>
              <div className="booking-list__meta">
                <BookingTypeMark booking={booking} t={t} />
                <button type="button" className="profile-chip profile-chip--primary" onClick={() => onOpenQr(booking)}>
                  <FaQrcode aria-hidden="true" />
                  {t("profilePage.sections.upcoming.qr", "Show QR")}
                </button>
                {!booking.isEventBooking && (
                  <button type="button" className="profile-chip" onClick={() => onOpenReschedule(booking)}>
                    {t("profilePage.sections.upcoming.reschedule", "Reschedule")}
                  </button>
                )}
                <button
                  type="button"
                  className="profile-chip profile-chip--alert"
                  onClick={() => onOpenCancel(booking)}
                >
                  {t("profilePage.sections.upcoming.cancel", "Cancel")}
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="profile-empty">
          {t("profilePage.sections.upcoming.empty", "No bookings yet. Time to plan the next paddle!")}
        </p>
      )}
    </div>
  </article>
);

export const PastRentalsCard = ({ pastRentals, t }) => (
  <article className="profile-card profile-card--scrollable">
    <header>
      <h2>{t("profilePage.sections.past.title", "Past rentals")}</h2>
    </header>
    <div className="profile-card__scroll">
      {pastRentals.length ? (
        <ul className="compact-list">
          {pastRentals.map((rental) => (
            <li key={rental.id}>
              <div>
                <h3>{rental.type}</h3>
                <p>
                  {(rental.displayDate ?? rental.date) || ""} · {rental.time} · {rental.duration}
                </p>
                <BookingLocationLink booking={rental} className="past-location" />
              </div>
              <div className="compact-list__meta">
                <BookingTypeMark booking={rental} t={t} />
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="profile-empty">
          {t("profilePage.sections.past.empty", "Your completed rides will appear here.")}
        </p>
      )}
    </div>
  </article>
);

export const InvoicesCard = ({ t }) => (
  <article className="profile-card profile-card--scrollable">
    <header>
      <h2>
        <LuFileText aria-hidden="true" /> {t("profilePage.sections.invoices.title", "Invoices")}
      </h2>
    </header>
    <div className="profile-card__scroll">
      <p className="profile-empty">
        {t("profilePage.sections.invoices.empty", "No invoices yet. Once you paddle, they’ll appear here.")}
      </p>
    </div>
  </article>
);

export const ReceiptsCard = ({
  receipts = [],
  isLoading = false,
  error = "",
  onDownload = () => {},
  onRefresh = () => {},
  downloadingId = null,
  t,
}) => (
  <article className="profile-card profile-card--scrollable profile-card--receipts">
    <header>
      <h2>
        <LuFileText aria-hidden="true" /> {t("profilePage.sections.receipts.title", "Receipts")}
      </h2>
      <button type="button" className="profile-link" onClick={onRefresh}>
        {t("profilePage.sections.receipts.refresh", "Refresh")}
      </button>
    </header>
    <div className="profile-card__scroll">
      {isLoading ? (
        <p className="profile-empty">
          {t("profilePage.sections.receipts.loading", "Fetching your receipts...")}
        </p>
      ) : error ? (
        <p className="profile-empty profile-empty--alert">
          {error}
        </p>
      ) : receipts.length ? (
        <ul className="compact-list">
          {receipts.map((receipt) => {
            const bookingId = receipt?.bookingId || receipt?.id;
            const when = receipt?.paymentUtc
              ? new Date(receipt.paymentUtc)
              : receipt?.startUtc
                ? new Date(receipt.startUtc)
                : null;
            const whenDateLabel = when && !Number.isNaN(when.getTime())
              ? when.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
              : "";
            const whenTimeLabel = when && !Number.isNaN(when.getTime())
              ? when.toLocaleTimeString(undefined, {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                })
              : "";
            const amountLabel =
              typeof receipt?.amount === "number" && receipt?.currency
                ? `${receipt.amount.toFixed(2)} ${receipt.currency}`
                : "";
            const bookingIdLabel = typeof bookingId === "string" ? bookingId : bookingId ? String(bookingId) : "";
            const bookingIdShort = bookingIdLabel ? bookingIdLabel.slice(0, 14) : "";
            const downloadLabel =
              downloadingId === bookingId
                ? t("profilePage.sections.receipts.downloading", "Downloading…")
                : t("profilePage.sections.receipts.download", "Download");

            return (
              <li key={bookingId || receipt?.fileName || whenDateLabel}>
                <button
                  type="button"
                  className="receipt-download"
                  onClick={() => onDownload(bookingId)}
                  disabled={!bookingId || downloadingId === bookingId}
                  aria-label={downloadLabel}
                  title={downloadLabel}
                >
                  <LuDownload aria-hidden="true" />
                </button>
                <div>
                  <h3>{receipt?.locationName || t("profilePage.sections.receipts.unknownLocation", "Receipt")}</h3>
                  <p>
                    {whenDateLabel}
                    {whenTimeLabel ? ` · ${whenTimeLabel}` : ""}
                    {amountLabel ? ` · ${amountLabel}` : ""}
                  </p>
                  {bookingIdShort ? (
                    <span className="receipt-meta" title={bookingIdLabel}>
                      {t("profilePage.sections.receipts.bookingId", "Booking")} {bookingIdShort}
                    </span>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="profile-empty">
          {t("profilePage.sections.receipts.empty", "No receipts yet. After your booking is paid, receipts will appear here.")}
        </p>
      )}
    </div>
  </article>
);

export const QuickActionsCard = ({ onEditProfile, onChangePassword, onLogout, t }) => (
  <article className="profile-card profile-card--actions">
    <header>
      <h2>{t("profilePage.quickActions.title", "Quick actions")}</h2>
    </header>
    <div className="action-buttons">
      <button type="button" className="profile-btn profile-btn--ghost" onClick={onEditProfile}>
        <LuPencil aria-hidden="true" />
        {t("profilePage.quickActions.editProfile", "Edit profile")}
      </button>
      <button type="button" className="profile-btn profile-btn--ghost" onClick={onChangePassword}>
        <LuKey aria-hidden="true" />
        {t("profilePage.quickActions.changePassword", "Change password")}
      </button>
      <button type="button" className="profile-btn profile-btn--danger" onClick={onLogout}>
        <LuLogOut aria-hidden="true" />
        {t("profilePage.quickActions.logout", "Log out")}
      </button>
    </div>
  </article>
);
